import { useStore } from "@/stores/useStore";
import { auth, authActions } from "@/services/auth";
import { signInWithEmailAndPassword } from "firebase/auth";
import { captureException, sentry } from "@/services/sentry";
import { User } from "@/types/v2";
import { toast } from "@/hooks/useToast";
import { user, user as userRepository } from "@/repositories/user";
import { httpsCallables } from "@/repositories/httpsCallables";
import { analyticsRepository } from "@/repositories/analytics";
import { useCallback, useMemo, useState } from "react";

/**
 * Custom hook providing authentication functions and state management for the application.
 * This hook leverages Firebase Authentication to handle common operations such as login, logout, signup,
 * password reset, and email verification, alongside managing user state and permissions.
 * @returns {object} An object containing the following properties and methods:
 * - `authUser`: The currently authenticated user object or `undefined` if no user is logged in.
 * - `authState`: The current state of authentication, such as "authenticated", "unauthenticated", or "signingIn".
 * - `authError`: The latest error message encountered during authentication operations, or `undefined` if no error exists.
 * - `authClaims`: Object containing the permissions and claims associated with the current user, or `undefined` if no user is authenticated.
 * - `initialized`: A boolean indicating whether the authentication system has been initialized and ready for use.
 * - `login(email: string, password: string): Promise<void>`: Function to authenticate a user using email and password.
 * - `logout(): void`: Function to log out the currently authenticated user and reset the application state.
 * - `sendVerificationEmail(): void`: Function to send a verification email to the currently authenticated user's email address.
 * - `signup(params: object): Promise<void>`: Function to create a user account with specified details, including shop registration.
 * - `resetPassword(email: string): Promise<void>`: Function to send a password reset email to the specified user.
 */
export function useAuth() {
  const {
    authUser,
    setAuthUser,
    authState,
    setAuthState,
    authError,
    setAuthError,
    authClaims,
    setAuthClaims,
    initialized,
    setInitialized,
  } = useStore();
  const [status, setStatus] = useState<
    "idle" | "verifying" | "success" | "error"
  >("idle");

  const verificationCache = useMemo(() => new Set(), []);
  const origin = window.location.origin;

  /**
   * Authenticates a user with the provided email and password.
   * @param {string} email - The email address of the user attempting to log in.
   * @param {string} password - The password associated with the provided email.
   * @returns {Promise<void>} A promise that resolves once the login process is complete.
   */
  async function login(email: string, password: string): Promise<void> {
    setAuthState("signingIn");
    setAuthError(undefined);

    await signInWithEmailAndPassword(auth, email, password)
      .then((userCredential) => {
        const user = userCredential.user;
        console.log("User logged in:", user.email);
        sentry.setUser({
          email: userCredential.user.email ?? "",
          id: userCredential.user.uid,
        });
        analyticsRepository.logAnalyticsEvent("login");
        analyticsRepository.setAnalyticsUserId(userCredential.user.uid);
        setAuthState(
          userCredential.user.emailVerified ? "authenticated" : "unverified"
        );
        setAuthUser({
          uid: userCredential.user.uid,
          email: userCredential.user.email ?? email,
          emailVerified: userCredential.user.emailVerified,
        });
        setInitialized(true);
      })
      .catch((error) => {
        const errorCode = error.code;
        const errorMessage = error.message;
        console.error("Error logging in user:", errorCode, errorMessage);
        sentry.setUser({});
        setAuthState("unauthenticated");
        if (errorCode === "auth/user-disabled") {
          setAuthError("User account is disabled. Contact support.");
        } else if (errorCode === "auth/user-not-found") {
          setAuthError("Login failed. User account does not exist.");
        } else if (errorCode === "auth/wrong-password") {
          setAuthError("Login failed. The email or password is incorrect");
        } else if (errorCode === "auth/too-many-requests") {
          setAuthError(
            "Too many unsuccessful login attempts. Please try again later."
          );
        } else if (errorCode === "auth/network-request-failed") {
          setAuthError(
            "Network error occurred. Please check your connection and try again."
          );
        } else {
          setAuthError("Login failed. The email or password is incorrect");
        }
        setInitialized(true);
      });
    return;
  }

  /**
   * Logs out the currently authenticated user by clearing the authentication state,
   * resetting the user and claims information, and reloading the window.
   * Updates the application state to reflect the unauthenticated state.
   * @returns {void} No return value.
   */
  function logout(): void {
    setAuthState("signingOut");
    setAuthError(undefined);
    auth
      .signOut()
      .then(() => {
        sentry.setUser({});
        analyticsRepository.logAnalyticsEvent("logout");
        analyticsRepository.setAnalyticsUserId("");
        console.log("User signed out");
        window.location.reload();
      })
      .catch(() => {
        setAuthError("Logout failed");
      })
      .finally(() => {
        setAuthState("unauthenticated");
        setAuthUser(undefined);
        setAuthClaims(undefined);
      });
  }

  /**
   * Registers a new user by creating an authentication user and storing user details in the database.
   * @param {object} params - The parameters for user registration.
   * @param {string} params.email - The email address of the new user.
   * @param {string} params.password - The password for the new user.
   * @param {string} params.firstName - The first name of the new user.
   * @param {string} params.lastName - The last name of the new user.
   * @returns {Promise<void>} Resolves when the user is successfully registered, or throws an error if registration fails.
   */
  async function register({
    email,
    password,
    firstName,
    lastName,
  }: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }) {
    setAuthError(undefined);
    analyticsRepository.logAnalyticsEvent("register");

    // Need to create the auth user and user in firebase. Shop will be created later.
    const newUser = await authActions
      .createUserWithEmailAndPassword(email, password)
      .catch((e) => {
        const error =
          e.message === "Firebase: Error (auth/email-already-in-use)."
            ? "Email already in use"
            : e.message;

        setAuthError(error);
        toast({
          title: "Registration Error",
          description: error,
          variant: "destructive",
        });
      });

    if (!newUser) {
      throw new Error("Unable to create auth user");
    }

    const dbUser: Partial<User> = {
      id: newUser.user.uid,
      uid: newUser.user.uid,
      email: email,
      displayName: firstName + " " + lastName,
      firstName: firstName,
      lastName: lastName,
      lastLoginTime: new Date(),
      role: "user",
    };
    await userRepository.create(dbUser);

    await new Promise((resolve) => setTimeout(resolve, 2000));
    return;
  }

  /**
   * Sends an invitation email to the specified email address.
   * @param {string} email The email address of the recipient to send the invite to.
   * @returns {Promise<any>} A promise that resolves with the response from the email sending operation.
   */
  async function sendInviteEmail(email: string) {
    const redirectLink = `${origin ?? "https://app.shopherodashboard.com"}/resetpassword?template=inviteUser`;
    try {
      await httpsCallables.sendInviteEmail({ email, redirectLink });
      toast({
        title: "User invite email sent!",
        variant: "success",
      });
    } catch (e) {
      captureException(e);
      toast({
        title:
          "Email invite unable to send. Try again in a few minutes or contact support.",
        variant: "destructive",
      });
    }
  }

  /**
   * Sends a verification email to the currently authenticated user.
   * If a user is authenticated, an email verification link will be sent to their registered email address.
   * If no user is authenticated, an error message will be set.
   * @returns {Promise<void>} This method does not return a value.
   */
  function sendVerificationEmail(): Promise<void> {
    setAuthError(undefined);
    analyticsRepository.logAnalyticsEvent("sendVerificationEmail");
    const redirectLink = `${origin ?? "https://app.shopherodashboard.com"}`;

    if (auth.currentUser) {
      return httpsCallables
        .sendVerifyEmailEmail({ uid: auth.currentUser.uid, redirectLink })
        .then(() => {
          toast({
            title: "Email verification sent! Please check your email.",
            variant: "success",
          });
        });
    } else {
      setAuthError("No user identified to send email verification link.");
      return Promise.resolve();
    }
  }

  /**
   * Sends a password reset email to the specified email address.
   * @param {string} email - The email address to which the password reset email will be sent.
   * @returns {Promise<void>} A promise that resolves once the password reset process has been initiated.
   */
  async function requestPasswordReset(email: string): Promise<void> {
    setAuthError(undefined);
    analyticsRepository.logAnalyticsEvent("requestPasswordReset");
    const redirectLink = `${origin ?? "https://app.shopherodashboard.com"}/resetpassword?template=resetPassword`;

    httpsCallables
      .sendPasswordResetEmail({ email, redirectLink })
      .then((response) => {
        const data = response?.data;

        if (data && typeof data === "object" && "code" in data) {
          if (data.code === "not-found") {
            setAuthError("No user found with that email address.");
            toast({
              title:
                "We were unable to send the password reset email. Check the spelling of the email and try again.",
              variant: "destructive",
            });
            return;
          } else if (data.code === "failed-precondition") {
            setAuthError("User account is disabled. Contact support.");
            toast({
              title:
                "Your user account is disabled, so we were unable to send the password reset email. Contact support if this continues",
              variant: "destructive",
            });
            return;
          } else {
            setAuthError("Unknown Error. Contact support.");
            toast({
              title:
                "We were unable to send the password reset email. Contact support if this continues",
              variant: "destructive",
            });
            return;
          }
        }
        toast({
          title: "Password reset link sent! Please check your email.",
          variant: "success",
        });
      })
      .catch((e) => {
        console.error("Error sending password reset email:", e);
        setAuthError("Error sending password reset email");
        toast({
          title:
            "We were unable to send the password reset email. Check the spelling of the email and try again.",
          variant: "destructive",
        });
      });
  }

  const verifyPasswordResetCode = useCallback(
    async (oobCode: string) => {
      if (verificationCache.has(oobCode)) {
        console.log("OOB code already used. Skipping verification.");
        return;
      }
      try {
        setAuthError(undefined);
        analyticsRepository.logAnalyticsEvent("resettingPassword");
        verificationCache.add(oobCode);
        setStatus("verifying");
        await authActions.verifyPasswordResetCode(oobCode);
        setStatus("success");
      } catch (e) {
        captureException(e);
        setAuthError(
          "Unable to verify password reset code. Please request a new link and try again."
        );
        setStatus("error");
      }
    },
    [setAuthError, verificationCache]
  );

  const resetPassword = useCallback(
    async (oobCode: string, newPassword: string) => {
      try {
        setAuthError(undefined);
        await authActions.confirmPasswordReset(oobCode, newPassword);
        setStatus("success");
        toast({
          title: "Password reset successful",
          description:
            "Your password has been reset. Please login with your new password.",
          variant: "success",
        });
        await new Promise((resolve) => setTimeout(resolve, 2000));
      } catch (e) {
        captureException(e);
        setAuthError(
          "Unable to reset password. Please request a new link and try again."
        );
        setStatus("error");
      }
    },
    [setAuthError, setStatus]
  );

  /**
   * Verifies the provided email using the out-of-band (oob) code.
   * @param {string} oobCode - The out-of-band code sent for email verification.
   * @returns {Promise<void>} A promise that resolves upon successful email verification or rejects if an error occurs.
   */
  async function verifyEmail(oobCode: string): Promise<void> {
    if (verificationCache.has(oobCode)) {
      console.log("OOB code already used. Skipping verification.");
      return;
    }

    try {
      const authUser = auth.currentUser;
      if (!authUser) {
        return;
      }
      console.log("Verifying email with OOB code:", oobCode);
      verificationCache.add(oobCode);
      setStatus("verifying");
      analyticsRepository.logAnalyticsEvent("verifyingEmail");
      await authActions.verifyEmail(oobCode);
      await user.markEmailVerified(authUser.uid);
      setAuthState("authenticated");
      setStatus("success");
      toast({
        title: "Email Verified",
        description:
          "Your email has been verified. You can now continue to use Shop Hero Dashbaord.",
        variant: "success",
      });
      await new Promise((resolve) => setTimeout(resolve, 2000));
    } catch (e) {
      captureException(e);
      setAuthError(
        "Unable to verify email. Please request a new link and try again."
      );
      setStatus("error");
    }
    return;
  }

  return {
    authUser,
    authState,
    authError,
    authClaims,
    initialized,
    status,
    login,
    logout,
    sendVerificationEmail,
    sendInviteEmail,
    setStatus,
    register,
    requestPasswordReset,
    verifyPasswordResetCode,
    verifyEmail,
    resetPassword,
  };
}
